import React, { useEffect, useState, useMemo, useRef } from "react";
import { useLang } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { UserPlus, Send, Trash2, Pencil, Upload, FileText, Mail, Banknote } from "lucide-react";

const STATUS_META = {
  pending: { fr: "En attente", en: "Pending", color: "bg-amber-100 text-amber-800 border-amber-300" },
  confirmed: { fr: "Confirmé", en: "Confirmed", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  not_reenrolling: { fr: "Ne se réinscrit pas", en: "Not re-enrolling", color: "bg-rose-100 text-rose-800 border-rose-300" },
  hold: { fr: "En réflexion", en: "On hold", color: "bg-orange-100 text-orange-800 border-orange-300" },
  enrolled: { fr: "Inscription payée", en: "Enrolled", color: "bg-sky-100 text-sky-800 border-sky-300" },
};

const SCHOOL_YEARS = ["2025-2026", "2026-2027", "2027-2028"];

const emptyForm = {
  school_year: "2026-2027",
  parent_email: "", parent_first_name: "", parent_last_name: "", parent_phone: "",
  student_first_name: "", student_last_name: "",
  current_grade: "", next_grade: "",
  student_dob: "", notes: "",
};

export default function AdminReenrollment() {
  const { lang } = useLang();
  const { isSuperAdmin } = useAuth();
  const [year, setYear] = useState("2026-2027");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get(`/admin/reenrollments?school_year=${encodeURIComponent(year)}`)
      .then(r => { setItems(r.data.items); setSummary(r.data.summary); })
      .catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [year]); // eslint-disable-line

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm, school_year: year }); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      school_year: it.school_year,
      parent_email: it.parent_email,
      parent_first_name: it.parent_first_name,
      parent_last_name: it.parent_last_name,
      parent_phone: it.parent_phone || "",
      student_first_name: it.student_first_name,
      student_last_name: it.student_last_name,
      current_grade: it.current_grade,
      next_grade: it.next_grade,
      student_dob: it.student_dob || "",
      notes: it.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    const req = ["parent_email", "parent_first_name", "parent_last_name",
                 "student_first_name", "student_last_name", "current_grade", "next_grade"];
    for (const k of req) {
      if (!String(form[k] || "").trim()) {
        toast.error(lang === "fr" ? "Champs requis manquants" : "Required fields missing"); return;
      }
    }
    try {
      if (editing) {
        const patch = {};
        ["current_grade", "next_grade", "notes"].forEach(k => { if (form[k] !== editing[k]) patch[k] = form[k]; });
        await api.patch(`/admin/reenrollments/${editing.id}`, patch);
        toast.success(lang === "fr" ? "Mis à jour" : "Updated");
      } else {
        await api.post("/admin/reenrollments", form);
        toast.success(lang === "fr" ? "Réinscription créée" : "Re-enrollment created");
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const updateStatus = async (it, newStatus) => {
    try {
      await api.patch(`/admin/reenrollments/${it.id}`, { status: newStatus });
      toast.success(lang === "fr" ? "Statut mis à jour" : "Status updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const remove = async (it) => {
    if (!window.confirm(lang === "fr"
      ? `Supprimer la réinscription de ${it.student_first_name} ${it.student_last_name} ?`
      : `Delete re-enrollment for ${it.student_first_name} ${it.student_last_name}?`)) return;
    try {
      await api.delete(`/admin/reenrollments/${it.id}`);
      toast.success(lang === "fr" ? "Supprimé" : "Deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const sendInvite = async (it) => {
    try {
      await api.post(`/admin/reenrollments/${it.id}/send-invitation`);
      toast.success(lang === "fr" ? "Invitation envoyée" : "Invitation sent");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const markPaid = async (it) => {
    const name = `${it.student_first_name} ${it.student_last_name}`;
    if (!window.confirm(lang === "fr"
      ? `Marquer l'acompte comme payé pour « ${name} » ?\n\nMontant : 295 € pour 1 cours, 390 € pour 2 cours.\nÀ utiliser uniquement si :\n— Stripe sandbox ne fonctionne pas\n— Paiement reçu hors ligne (virement, chèque)`
      : `Mark the deposit as paid for "${name}"?\n\nAmount: €295 for 1 course, €390 for 2 courses.\nUse only if Stripe is down or payment received offline.`)) return;
    try {
      await api.post(`/admin/reenrollments/${it.id}/mark-deposit-paid`);
      toast.success(lang === "fr" ? "Acompte marqué payé · statut « inscrit »" : "Deposit marked paid · status « enrolled »");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const sendAllPending = async () => {
    const pending = summary.pending || 0;
    if (!pending) { toast.info(lang === "fr" ? "Aucune réinscription en attente" : "No pending re-enrollments"); return; }
    if (!window.confirm(lang === "fr"
      ? `Envoyer une invitation à ${pending} familles ?`
      : `Send invitations to ${pending} families?`)) return;
    try {
      const r = await api.post(`/admin/reenrollments/send-pending-invitations?school_year=${encodeURIComponent(year)}`);
      toast.success(lang === "fr" ? `${r.data.sent} invitations envoyées` : `${r.data.sent} invitations sent`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  // CSV import: expects header row with these columns (any order):
  // school_year,parent_email,parent_first_name,parent_last_name,student_first_name,
  // student_last_name,current_grade,next_grade,parent_phone,student_dob,notes
  const importCsv = async () => {
    try {
      const lines = csvText.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) { toast.error(lang === "fr" ? "CSV vide ou sans données" : "Empty CSV"); return; }
      const header = lines[0].split(/[,;]/).map(h => h.trim().toLowerCase());
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(/[,;]/).map(c => c.trim());
        const o = {};
        header.forEach((h, idx) => { o[h] = cols[idx] || ""; });
        if (!o.parent_email) continue;
        rows.push({
          school_year: o.school_year || year,
          parent_email: o.parent_email,
          parent_first_name: o.parent_first_name,
          parent_last_name: o.parent_last_name,
          parent_phone: o.parent_phone || null,
          student_first_name: o.student_first_name,
          student_last_name: o.student_last_name,
          current_grade: o.current_grade,
          next_grade: o.next_grade,
          student_dob: o.student_dob || null,
          notes: o.notes || null,
        });
      }
      if (!rows.length) { toast.error(lang === "fr" ? "Aucune ligne valide" : "No valid row"); return; }
      const r = await api.post("/admin/reenrollments/import-csv", { rows });
      toast.success(lang === "fr"
        ? `${r.data.inserted} ajoutées · ${r.data.skipped} ignorées · ${r.data.errors.length} erreurs`
        : `${r.data.inserted} added · ${r.data.skipped} skipped · ${r.data.errors.length} errors`);
      setCsvOpen(false); setCsvText(""); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const onCsvFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    setCsvText(text);
  };

  const filtered = useMemo(() => {
    if (statusFilter === "all") return items;
    return items.filter(i => i.status === statusFilter);
  }, [items, statusFilter]);

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US") : "—";

  return (
    <div data-testid="admin-reenrollment">
      <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
        <div>
          <p className="overline mb-1">OFA France · Admin</p>
          <h1 className="font-serif text-3xl tracking-tight">
            {lang === "fr" ? "Réinscriptions" : "Re-enrollments"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            {lang === "fr"
              ? "Gérez la réinscription des élèves déjà à OFA. Aucun frais d'inscription, juste l'acompte (295 € pour 1 cours / 390 € pour 2 cours) + plan de paiement."
              : "Manage re-enrollment for existing OFA students. No application fee, just the deposit (€295 for 1 course / €390 for 2 courses) + payment plan."}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setCsvOpen(true)} data-testid="open-csv-btn">
            <Upload className="w-4 h-4 mr-1.5" />{lang === "fr" ? "Import CSV" : "Import CSV"}
          </Button>
          <Button onClick={openCreate} data-testid="create-reenroll-btn">
            <UserPlus className="w-4 h-4 mr-1.5" />{lang === "fr" ? "Ajouter un élève" : "Add a student"}
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <Card className="mb-5">
        <CardContent className="p-4 grid md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs">{lang === "fr" ? "Année scolaire" : "School year"}</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger data-testid="re-year"><SelectValue /></SelectTrigger>
              <SelectContent>{SCHOOL_YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">{lang === "fr" ? "Filtre statut" : "Status filter"}</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="re-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{lang === "fr" ? "Tous" : "All"}</SelectItem>
                {Object.entries(STATUS_META).map(([k, v]) =>
                  <SelectItem key={k} value={k}>{v[lang]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={sendAllPending} data-testid="send-all-pending">
            <Send className="w-4 h-4 mr-1.5" />
            {lang === "fr" ? `Envoyer aux ${summary.pending || 0} en attente` : `Send to ${summary.pending || 0} pending`}
          </Button>
          <div className="text-xs text-muted-foreground self-center">{items.length} total</div>
        </CardContent>
      </Card>

      {/* Summary chips */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="re-summary">
        {Object.entries(STATUS_META).map(([k, v]) => (
          <button
            key={k}
            onClick={() => setStatusFilter(statusFilter === k ? "all" : k)}
            className={`px-3 py-1.5 rounded-full border text-xs font-medium transition ${v.color} ${statusFilter === k ? "ring-2 ring-offset-1" : "hover:opacity-80"}`}
            data-testid={`re-chip-${k}`}
          >
            {v[lang]} : {summary[k] || 0}
          </button>
        ))}
      </div>

      {loading && <div className="text-center py-8 text-muted-foreground text-sm">{lang === "fr" ? "Chargement…" : "Loading…"}</div>}

      {!loading && filtered.length === 0 && (
        <Card>
          <CardContent className="p-10 text-center text-muted-foreground text-sm">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
            {lang === "fr" ? "Aucune réinscription pour cette sélection." : "No re-enrollment for this selection."}
          </CardContent>
        </Card>
      )}

      {!loading && filtered.length > 0 && (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="p-3 text-left">{lang === "fr" ? "Élève" : "Student"}</th>
                  <th className="p-3 text-left">{lang === "fr" ? "Niveau" : "Grade"}</th>
                  <th className="p-3 text-left">{lang === "fr" ? "Parent" : "Parent"}</th>
                  <th className="p-3 text-left">{lang === "fr" ? "Statut" : "Status"}</th>
                  <th className="p-3 text-left">{lang === "fr" ? "Invitation" : "Invitation"}</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(it => {
                  const meta = STATUS_META[it.status] || STATUS_META.pending;
                  return (
                    <tr key={it.id} className="border-t border-border hover:bg-secondary/20" data-testid={`re-row-${it.id}`}>
                      <td className="p-3 font-medium">
                        {it.student_first_name} {it.student_last_name}
                        {it.notes && <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">{it.notes}</div>}
                      </td>
                      <td className="p-3 text-xs whitespace-nowrap">
                        {it.current_grade} <span className="text-muted-foreground">→</span> <strong>{it.next_grade}</strong>
                      </td>
                      <td className="p-3 text-xs">
                        <div>{it.parent_first_name} {it.parent_last_name}</div>
                        <div className="text-muted-foreground">{it.parent_email}</div>
                      </td>
                      <td className="p-3">
                        <Select value={it.status} onValueChange={(v) => updateStatus(it, v)}>
                          <SelectTrigger data-testid={`re-status-${it.id}`} className="h-8 w-auto min-w-[140px]">
                            <Badge variant="outline" className={`${meta.color} text-[10px]`}>{meta[lang]}</Badge>
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS_META).map(([k, v]) =>
                              <SelectItem key={k} value={k}>{v[lang]}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-3 text-[11px] text-muted-foreground whitespace-nowrap">
                        {it.invitation_sent_at ? `📨 ${fmt(it.invitation_sent_at)}` : "—"}
                        {it.parent_responded_at && <div>↩ {fmt(it.parent_responded_at)}</div>}
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="icon" variant="ghost" onClick={() => sendInvite(it)} title={lang === "fr" ? "Envoyer invitation" : "Send invitation"} data-testid={`re-send-${it.id}`}>
                            <Mail className="w-3.5 h-3.5" />
                          </Button>
                          {isSuperAdmin && (it.status === "confirmed" || it.status === "pending") && (
                            <Button size="icon" variant="ghost" onClick={() => markPaid(it)}
                                    className="text-emerald-600 hover:text-emerald-700"
                                    title={lang === "fr" ? "Marquer acompte payé (test / hors-ligne)" : "Mark deposit paid (test / offline)"}
                                    data-testid={`re-markpaid-${it.id}`}>
                              <Banknote className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {it.status === "enrolled" && (
                            <Badge
                              className="bg-emerald-50 text-emerald-700 border-emerald-300 text-[10px] gap-1 px-2"
                              data-testid={`re-deposit-paid-${it.id}`}
                              title={it.deposit_paid_at ? `${lang === "fr" ? "Payé le" : "Paid on"} ${fmt(it.deposit_paid_at)}` : ""}
                            >
                              <Banknote className="w-3 h-3" />
                              {lang === "fr" ? "Acompte payé" : "Deposit paid"}
                            </Badge>
                          )}
                          <Button size="icon" variant="ghost" onClick={() => openEdit(it)} data-testid={`re-edit-${it.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          {isSuperAdmin && (
                            <Button size="icon" variant="ghost" onClick={() => remove(it)}
                                    className="text-destructive hover:text-destructive"
                                    data-testid={`re-delete-${it.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing
              ? (lang === "fr" ? "Modifier la réinscription" : "Edit re-enrollment")
              : (lang === "fr" ? "Nouvelle réinscription" : "New re-enrollment")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{lang === "fr" ? "Année scolaire" : "School year"}</Label>
                <Select value={form.school_year} onValueChange={v => setForm(f => ({ ...f, school_year: v }))} disabled={!!editing}>
                  <SelectTrigger data-testid="re-form-year"><SelectValue /></SelectTrigger>
                  <SelectContent>{SCHOOL_YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div></div>
              <div>
                <Label className="text-xs">{lang === "fr" ? "Email parent" : "Parent email"}</Label>
                <Input data-testid="re-form-email" type="email" value={form.parent_email} onChange={e => setForm(f => ({ ...f, parent_email: e.target.value }))} disabled={!!editing} />
              </div>
              <div>
                <Label className="text-xs">{lang === "fr" ? "Téléphone (optionnel)" : "Phone (optional)"}</Label>
                <Input data-testid="re-form-phone" value={form.parent_phone} onChange={e => setForm(f => ({ ...f, parent_phone: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">{lang === "fr" ? "Prénom parent" : "Parent first name"}</Label>
                <Input data-testid="re-form-pfirst" value={form.parent_first_name} onChange={e => setForm(f => ({ ...f, parent_first_name: e.target.value }))} disabled={!!editing} />
              </div>
              <div>
                <Label className="text-xs">{lang === "fr" ? "Nom parent" : "Parent last name"}</Label>
                <Input data-testid="re-form-plast" value={form.parent_last_name} onChange={e => setForm(f => ({ ...f, parent_last_name: e.target.value }))} disabled={!!editing} />
              </div>
              <div>
                <Label className="text-xs">{lang === "fr" ? "Prénom élève" : "Student first name"}</Label>
                <Input data-testid="re-form-sfirst" value={form.student_first_name} onChange={e => setForm(f => ({ ...f, student_first_name: e.target.value }))} disabled={!!editing} />
              </div>
              <div>
                <Label className="text-xs">{lang === "fr" ? "Nom élève" : "Student last name"}</Label>
                <Input data-testid="re-form-slast" value={form.student_last_name} onChange={e => setForm(f => ({ ...f, student_last_name: e.target.value }))} disabled={!!editing} />
              </div>
              <div>
                <Label className="text-xs">{lang === "fr" ? "Classe actuelle (2025-2026)" : "Current grade (2025-26)"}</Label>
                <Input data-testid="re-form-cur" placeholder="CM2 · 6e · 2nde…" value={form.current_grade} onChange={e => setForm(f => ({ ...f, current_grade: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">{lang === "fr" ? "Classe suivante" : "Next grade"}</Label>
                <Input data-testid="re-form-next" placeholder="6e · 5e · 1ère…" value={form.next_grade} onChange={e => setForm(f => ({ ...f, next_grade: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-xs">{lang === "fr" ? "Notes internes" : "Internal notes"}</Label>
              <Textarea data-testid="re-form-notes" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <Button onClick={save} className="w-full" data-testid="re-form-save">
              {lang === "fr" ? "Enregistrer" : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CSV import dialog */}
      <Dialog open={csvOpen} onOpenChange={setCsvOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{lang === "fr" ? "Import CSV — Réinscriptions" : "CSV import — Re-enrollments"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground bg-secondary/30 rounded p-3 leading-relaxed">
              {lang === "fr"
                ? "Format attendu (1ère ligne = en-têtes, virgule ou point-virgule comme séparateur) :"
                : "Expected format (1st row = headers, comma or semicolon as separator):"}
              <code className="block mt-2 text-[10px] font-mono break-all">
                parent_email,parent_first_name,parent_last_name,student_first_name,student_last_name,current_grade,next_grade,parent_phone,notes
              </code>
              <div className="mt-2">
                <strong>{lang === "fr" ? "Exemple :" : "Example:"}</strong>
                <code className="block mt-1 text-[10px] font-mono break-all">
                  marie@example.com,Marie,Dupont,Lucas,Dupont,CM2,6e,+33612345678,Bon profil
                </code>
              </div>
            </div>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onCsvFile} className="hidden" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} className="w-full" data-testid="csv-upload-file">
              <Upload className="w-4 h-4 mr-1.5" />
              {lang === "fr" ? "Choisir un fichier CSV" : "Choose CSV file"}
            </Button>
            <Textarea data-testid="csv-textarea" rows={8} value={csvText} onChange={e => setCsvText(e.target.value)} placeholder={lang === "fr" ? "Ou collez le CSV ici…" : "Or paste CSV here…"} className="font-mono text-xs" />
            <Button onClick={importCsv} className="w-full" data-testid="csv-import-btn" disabled={!csvText.trim()}>
              {lang === "fr" ? "Importer" : "Import"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
